import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

function UsersManagement({
    users,
    theme,
    showInactiveUsers,
    setShowInactiveUsers,
    setShowAddUser,
    handleOpenEditUser,
    handleDeleteUser
}) {
    const { t } = useLanguage();
    const [searchTerm, setSearchTerm] = useState('');

    // Filter users based on search term
    const matchesSearch = (user) => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        return [
            user.user_name,
            user.email,
            user.role_title,
            user.cell_phone,
            user.active ? t('active') : t('inactive')
        ].some(field =>
            field && field.toString().toLowerCase().includes(search)
        );
    };

    const filteredUsers = users.filter(matchesSearch);
    return (
        <div style={{
            background: theme.colors.surface,
            padding: '0rem',
            borderRadius: theme.borderRadius || 8,
            boxShadow: theme.boxShadow,
            marginBottom: '1.5rem'
        }}>
            <div style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <button
                        onClick={() => setShowAddUser(true)}
                        style={{
                            marginTop: '0.5rem',
                            padding: '0.5rem 1rem',
                            backgroundColor: theme.colors.primary,
                            color: theme.colors.surface,
                            border: 'none',
                            borderRadius: theme.borderRadius || 8,
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: 'bold'
                        }}
                    >
                        + {t('addUser')}
                    </button>
                </div>

                {/* Search Input */}
                <div style={{ marginBottom: '1rem' }}>
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder={t('searchUsers')}
                        style={{
                            width: '100%',
                            padding: '0.75rem',
                            fontSize: '0.95rem',
                            border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                            borderRadius: theme?.borderRadius || 4,
                            backgroundColor: theme?.colors?.surface || '#fff',
                            color: theme?.colors?.text || '#000',
                            outline: 'none'
                        }}
                        onFocus={(e) => {
                            e.target.style.borderColor = theme?.colors?.primary || '#1976d2';
                        }}
                        onBlur={(e) => {
                            e.target.style.borderColor = theme?.colors?.border || '#ddd';
                        }}
                    />
                    {searchTerm && (
                        <div style={{
                            marginTop: '0.5rem',
                            fontSize: '0.85rem',
                            color: theme?.colors?.textSecondary || '#666'
                        }}>
                            {t('showing')} {filteredUsers.length} {t('of')} {users.length} {t('users').toLowerCase()}
                        </div>
                    )}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <label style={{ fontSize: '0.9rem', color: theme.colors.text }}>
                        <input
                            type="checkbox"
                            checked={showInactiveUsers}
                            onChange={(e) => setShowInactiveUsers(e.target.checked)}
                            style={{ marginRight: '0.5rem' }}
                        />
                        {t('showInactiveUsers')}
                    </label>
                </div>
            </div>
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {filteredUsers.map(user => (
                    <div key={user.id} style={{
                        padding: '0.75rem',
                        marginBottom: '0.5rem',
                        background: theme.colors.background,
                        borderRadius: theme.borderRadius || 6,
                        border: `1px solid ${theme.colors.primary}20`,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <div>
                            <strong>{user.user_name || user.email}</strong>
                            <div style={{ fontSize: '0.8rem', color: theme.colors.textSecondary }}>
                                {t('email')}: {user.email} | {t('role')}: {user.role_title || t('unknown')}
                            </div>
                            <div style={{ fontSize: '0.8rem', color: theme.colors.textSecondary }}>
                                {t('created')}: {new Date(user.created).toLocaleDateString()}
                            </div>
                            <div style={{ fontSize: '0.8rem', color: theme.colors.textSecondary }}>
                                {t('phone')}: {user.cell_phone || t('unknown')}
                            </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <div style={{
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                background: user.active ? '#e8f5e8' : '#ffebee',
                                color: user.active ? '#2e7d32' : '#c62828',
                                fontSize: '0.8rem',
                                fontWeight: 'bold'
                            }}>
                                {user.active ? t('active') : t('inactive')}
                            </div>
                            <button
                                onClick={() => handleOpenEditUser(user)}
                                style={{
                                    padding: '0.25rem 0.5rem',
                                    backgroundColor: theme.colors.secondary || '#666',
                                    color: theme.colors.surface,
                                    border: 'none',
                                    borderRadius: theme.borderRadius || 4,
                                    cursor: 'pointer',
                                    fontSize: '0.7rem',
                                    fontWeight: 'bold'
                                }}
                                title={t('editUser')}
                            >
                                ✏️
                            </button>
                            <button
                                onClick={() => handleDeleteUser(user.id)}
                                disabled={user.id === 1}
                                style={{
                                    padding: '0.25rem 0.5rem',
                                    backgroundColor: user.id === 1 ? '#ccc' : '#d32f2f',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: theme.borderRadius || 4,
                                    cursor: user.id === 1 ? 'not-allowed' : 'pointer',
                                    fontSize: '0.7rem',
                                    fontWeight: 'bold'
                                }}
                                title={user.id === 1 ? t('cannotDeleteAdminUser') : t('deleteUser')}
                            >
                                🗑️
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default UsersManagement;